// shared/StockUI.tsx
// ─────────────────────────────────────────────────────────
// Shared UI primitives for Stock Calculator suite.
// Import from this file in any .tsx page component.
// ─────────────────────────────────────────────────────────

import React, { ReactNode } from "react";

// ── Highlight ─────────────────────────────────────────────
export const Highlight = ({ children }: { children: ReactNode }) => (
  <span
    style={{
      color: "#fff",
      background: "rgba(255,255,255,0.15)",
      padding: "0 10px",
      borderRadius: 6,
    }}
  >
    {children}
  </span>
);

// ── PageHeader ────────────────────────────────────────────
export const PageHeader = ({
  title,
  subtitle,
  tag,
}: {
  title: ReactNode;
  subtitle?: string;
  tag?: string;
}) => (
  <header
    style={{
      background:
        "linear-gradient(135deg,#cc1414 0%,#a50f0f 60%,#8b0d0d 100%)",
      padding: "36px 24px 40px",
      textAlign: "center",
      position: "relative",
      overflow: "hidden",
      boxShadow: "0 4px 24px rgba(160,15,15,0.25)",
    }}
  >
    {/* Grid overlay */}
    <div
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        backgroundImage: `
          repeating-linear-gradient(0deg,transparent,transparent 29px,rgba(255,255,255,0.04) 29px,rgba(255,255,255,0.04) 30px),
          repeating-linear-gradient(90deg,transparent,transparent 29px,rgba(255,255,255,0.04) 29px,rgba(255,255,255,0.04) 30px)
        `,
      }}
    />
    {/* Decorative circles */}
    <div
      style={{
        position: "absolute", top: -30, right: -30,
        width: 120, height: 120, borderRadius: "50%",
        background: "rgba(255,255,255,0.06)", pointerEvents: "none",
      }}
    />
    <div
      style={{
        position: "absolute", bottom: -20, left: -20,
        width: 80, height: 80, borderRadius: "50%",
        background: "rgba(255,255,255,0.05)", pointerEvents: "none",
      }}
    />

    {/* Tag pill */}
    {tag && (
      <div
        style={{
          fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.2em",
          color: "rgba(255,255,255,0.75)", textTransform: "uppercase",
          background: "rgba(255,255,255,0.12)",
          border: "1px solid rgba(255,255,255,0.25)",
          padding: "4px 14px", borderRadius: 100,
          display: "inline-block", marginBottom: 16, position: "relative",
        }}
      >
        {tag}
      </div>
    )}

    <h1
      style={{
        fontSize: "clamp(26px,6vw,44px)", fontWeight: 800,
        letterSpacing: "-0.03em", lineHeight: 1.1,
        color: "#fff", position: "relative",
      }}
    >
      {title}
    </h1>

    {subtitle && (
      <p
        style={{
          color: "rgba(255,255,255,0.65)", fontSize: 13,
          marginTop: 10, fontFamily: "var(--mono)", position: "relative",
        }}
      >
        {subtitle}
      </p>
    )}
  </header>
);

// ── AccentStrip ───────────────────────────────────────────
export const AccentStrip = () => (
  <div
    style={{ height: 4, background: "linear-gradient(90deg,#cc1414 0%,#4169e1 100%)" }}
  />
);

// ── Card ──────────────────────────────────────────────────
export const Card = ({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) => (
  <div
    className={className}
    style={{
      background: "var(--card)",
      border: "1px solid var(--border)",
      borderRadius: 16,
      padding: 24,
      boxShadow: "0 2px 12px rgba(65,105,225,0.07)",
      ...style,
    }}
  >
    {children}
  </div>
);

// ── SectionTitle ──────────────────────────────────────────
export const SectionTitle = ({
  children,
  badge,
}: {
  children: ReactNode;
  badge?: string;
}) => (
  <div
    style={{
      fontSize: 11, fontFamily: "var(--mono)", letterSpacing: "0.14em",
      textTransform: "uppercase", color: "var(--muted)",
      marginBottom: 20, display: "flex", alignItems: "center", gap: 8,
    }}
  >
    <span
      style={{
        width: 3, height: 14, background: "var(--red)",
        borderRadius: 2, display: "inline-block", flexShrink: 0,
      }}
    />
    {children}
    {badge && (
      <span
        style={{
          marginLeft: "auto", fontFamily: "var(--mono)", fontSize: 9,
          fontWeight: 700, letterSpacing: "0.1em",
          padding: "2px 8px", borderRadius: 100,
          background: "rgba(65,105,225,0.1)", color: "var(--royal)",
          border: "1px solid rgba(65,105,225,0.2)",
        }}
      >
        {badge}
      </span>
    )}
  </div>
);

// ── Field ─────────────────────────────────────────────────
export const Field = ({
  label, id, type = "text", value, onChange,
  placeholder, step, fullWidth = false,
}: {
  label: string;
  id: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  step?: string;
  fullWidth?: boolean;
}) => (
  <div
    style={{
      display: "flex", flexDirection: "column", gap: 6,
      gridColumn: fullWidth ? "1 / -1" : undefined,
    }}
  >
    <label
      htmlFor={id}
      style={{
        fontSize: 11, fontFamily: "var(--mono)", color: "var(--royal)",
        letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700,
      }}
    >
      {label}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      placeholder={placeholder}
      step={step}
      onChange={(e) => onChange(e.target.value)}
      style={{
        background: "#fff", border: "2px solid var(--royal)",
        color: "var(--royal)", fontFamily: "var(--mono)",
        fontSize: 16, padding: "11px 12px", borderRadius: 10,
        outline: "none", width: "100%",
        transition: "border-color 0.2s,box-shadow 0.2s",
        boxSizing: "border-box", minWidth: 0,
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = "var(--royal-dark)";
        e.currentTarget.style.boxShadow = "0 0 0 3px rgba(65,105,225,0.15)";
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = "var(--royal)";
        e.currentTarget.style.boxShadow = "none";
      }}
    />
  </div>
);

// ── StatTile ──────────────────────────────────────────────
export const StatTile = ({
  label, value, sub,
  color = "var(--text)",
  bg = "#f8f9fc",
}: {
  label: string;
  value: string;
  sub?: string;
  color?: string;
  bg?: string;
}) => (
  <div
    style={{
      background: bg, border: "1px solid var(--border)",
      borderRadius: 12, padding: "14px 16px",
      display: "flex", flexDirection: "column", gap: 3,
    }}
  >
    <span
      style={{
        fontFamily: "var(--mono)", fontSize: 9,
        letterSpacing: "0.12em", textTransform: "uppercase",
        color: "var(--muted)",
      }}
    >
      {label}
    </span>
    <span
      style={{ fontFamily: "var(--mono)", fontSize: 20, fontWeight: 700, color, lineHeight: 1.2 }}
    >
      {value}
    </span>
    {sub && (
      <span style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--muted)" }}>
        {sub}
      </span>
    )}
  </div>
);

// ── InfoBox ───────────────────────────────────────────────
export const InfoBox = ({
  color, bg, border, title, children,
}: {
  color: string;
  bg: string;
  border: string;
  title: string;
  children: ReactNode;
}) => (
  <div
    style={{
      background: bg, border: `1.5px solid ${border}`,
      borderRadius: 12, padding: 16,
      borderLeft: `4px solid ${color}`,
    }}
  >
    <div
      style={{ fontFamily: "var(--sans)", fontWeight: 800, fontSize: 13, color, marginBottom: 6 }}
    >
      {title}
    </div>
    <p style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--text)", lineHeight: 1.7 }}>
      {children}
    </p>
  </div>
);

// ── ErrorBanner ───────────────────────────────────────────
export const ErrorBanner = ({ message }: { message: string }) => (
  <p
    style={{
      marginTop: 14, fontFamily: "var(--mono)", fontSize: 12,
      color: "var(--red)", background: "rgba(204,20,20,0.06)",
      border: "1px solid rgba(204,20,20,0.2)", borderRadius: 8, padding: "8px 12px",
    }}
  >
    ⚠️ {message}
  </p>
);

// ── Disclaimer ────────────────────────────────────────────
export const Disclaimer = ({ text }: { text?: string }) => (
  <p
    style={{
      fontFamily: "var(--mono)", fontSize: 10, color: "var(--muted)",
      textAlign: "center", opacity: 0.6, padding: "0 16px",
    }}
  >
    {text ?? "⚠️ Not financial advice. For educational purposes only. Always do your own research."}
  </p>
);

// ── PrimaryButton ─────────────────────────────────────────
export const PrimaryButton = ({
  onClick, disabled = false, children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      width: "100%", padding: 15,
      background: disabled
        ? "var(--border)"
        : "linear-gradient(135deg,var(--red) 0%,var(--red-dark) 100%)",
      color: disabled ? "var(--muted)" : "#fff",
      fontFamily: "var(--sans)", fontWeight: 800, fontSize: 15,
      letterSpacing: "0.05em", border: "none", borderRadius: 12,
      cursor: disabled ? "not-allowed" : "pointer",
      transition: "transform 0.15s,box-shadow 0.15s",
      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
      boxShadow: disabled ? "none" : "0 4px 14px rgba(204,20,20,0.3)",
    }}
    onMouseEnter={(e) => {
      if (!disabled) {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 8px 24px rgba(204,20,20,0.35)";
      }
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = disabled ? "none" : "0 4px 14px rgba(204,20,20,0.3)";
    }}
    onMouseDown={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
    }}
  >
    {children}
  </button>
);

// ── SecondaryButton (PDF Export) ──────────────────────────
export const SecondaryButton = ({
  onClick, disabled = false, children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      padding: "12px 24px",
      background: disabled
        ? "var(--border)"
        : "linear-gradient(135deg,var(--royal) 0%,var(--royal-dark) 100%)",
      color: disabled ? "var(--muted)" : "#fff",
      fontFamily: "var(--sans)", fontWeight: 800, fontSize: 14,
      letterSpacing: "0.04em", border: "none", borderRadius: 12,
      cursor: disabled ? "not-allowed" : "pointer",
      transition: "transform 0.15s,box-shadow 0.15s",
      display: "flex", alignItems: "center", gap: 8,
      boxShadow: disabled ? "none" : "0 4px 14px rgba(65,105,225,0.3)",
      whiteSpace: "nowrap", flexShrink: 0,
    }}
    onMouseEnter={(e) => {
      if (!disabled) {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 8px 20px rgba(65,105,225,0.35)";
      }
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = disabled ? "none" : "0 4px 14px rgba(65,105,225,0.3)";
    }}
  >
    {children}
  </button>
);

// ── PdfExportPanel ────────────────────────────────────────
export const PdfExportPanel = ({
  onExport,
  isExporting,
  title = "📄 Export Strategy Report",
  subtitle = "Download a clean PDF summary of this analysis",
}: {
  onExport: () => void;
  isExporting: boolean;
  title?: string;
  subtitle?: string;
}) => (
  <div
    style={{
      background: "var(--card)", border: "1.5px dashed var(--border)",
      borderRadius: 16, padding: 20,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: 16, flexWrap: "wrap",
    }}
  >
    <div>
      <p style={{ fontFamily: "var(--sans)", fontWeight: 700, fontSize: 14, color: "var(--text)", marginBottom: 3 }}>
        {title}
      </p>
      <p style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--muted)" }}>
        {subtitle}
      </p>
    </div>
    <SecondaryButton onClick={onExport} disabled={isExporting}>
      {isExporting ? (
        <>
          <span className="spinner">⟳</span> Generating…
        </>
      ) : (
        <>⬇ Download PDF</>
      )}
    </SecondaryButton>
  </div>
);

// ── Global CSS (inject once at app root) ──────────────────
export const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Syne:wght@400;600;800&display=swap');

    :root {
      --bg:         #ffffff;
      --surface:    #f8f9fc;
      --card:       #ffffff;
      --border:     #dde1f0;
      --red:        #cc1414;
      --red-dark:   #a50f0f;
      --royal:      #4169e1;
      --royal-dark: #2a4fc7;
      --green:      #16a34a;
      --text:       #1a1a2e;
      --muted:      #6b7280;
      --mono:       'Space Mono', monospace;
      --sans:       'Syne', sans-serif;
    }

    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

    input[type=number]::-webkit-inner-spin-button,
    input[type=number]::-webkit-outer-spin-button { opacity: 0.4; }
    input::placeholder { color: #a5b4fc !important; opacity: 1; }

    /* ── Form grid: 2-col ≥520px, 1-col below ── */
    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 14px;
    }
    @media (max-width: 519px) {
      .form-grid { grid-template-columns: 1fr; }
      .form-grid > * { grid-column: auto !important; }
    }

    /* ── Stat grids ── */
    .stat-grid2 { display: grid; grid-template-columns: repeat(2,1fr); gap: 12px; }
    .stat-grid3 { display: grid; grid-template-columns: repeat(3,1fr); gap: 12px; }
    @media (max-width: 560px) {
      .stat-grid3 { grid-template-columns: repeat(2,1fr); }
    }
    @media (max-width: 380px) {
      .stat-grid2, .stat-grid3 { grid-template-columns: 1fr; }
    }

    /* ── Animations ── */
    @keyframes fadeDown { from{opacity:0;transform:translateY(-16px)} to{opacity:1;transform:translateY(0)} }
    @keyframes fadeUp   { from{opacity:0;transform:translateY( 16px)} to{opacity:1;transform:translateY(0)} }
    @keyframes spin     { to{transform:rotate(360deg)} }

    .fade-down  { animation: fadeDown 0.6s ease both; }
    .fade-up    { animation: fadeUp   0.5s       ease both; }
    .fade-up-1  { animation: fadeUp   0.5s 0.05s ease both; }
    .fade-up-2  { animation: fadeUp   0.5s 0.10s ease both; }
    .fade-up-3  { animation: fadeUp   0.5s 0.15s ease both; }
    .spinner    { animation: spin 0.8s linear infinite; display: inline-block; }
  `}</style>
);
